<template>
  <div
    ref="containerRef"
    :class="rootClasses"
    :style="containerStyle"
    role="region"
    :aria-label="ariaLabel"
    @mouseenter="handleMouseEnter"
    @mouseleave="handleMouseLeave"
  >
    <template v-if="fadeOut">
      <div
        aria-hidden
        :class="[
          'pointer-events-none absolute inset-y-0 left-0 z-1',
          'w-[clamp(24px,8%,120px)]',
          'bg-[linear-gradient(to_right,var(--logoloop-fadeColor,var(--logoloop-fadeColorAuto))_0%,rgba(0,0,0,0)_100%)]'
        ]"
      />
      <div
        aria-hidden
        :class="[
          'pointer-events-none absolute inset-y-0 right-0 z-1',
          'w-[clamp(24px,8%,120px)]',
          'bg-[linear-gradient(to_left,var(--logoloop-fadeColor,var(--logoloop-fadeColorAuto))_0%,rgba(0,0,0,0)_100%)]'
        ]"
      />
    </template>

    <motion.div
      :key="animationKey"
      ref="trackRef"
      :class="['flex w-max select-none', 'motion-reduce:transform-none']"
      :initial="{ x: currentX }"
      :animate="{ x: animateX }"
      :transition="transitionConfig"
      @update="handleAnimationUpdate"
    >
      <ul
        v-for="copyIndex in copyCount"
        :key="`copy-${copyIndex - 1}`"
        :ref="
          (el) => {
            if (copyIndex === 1) seqRef = el as HTMLUListElement
          }
        "
        class="flex items-center"
        role="list"
        :aria-hidden="copyIndex > 1"
      >
        <li
          v-for="(item, itemIndex) in logos"
          :key="`${copyIndex - 1}-${itemIndex}`"
          :class="[
            'flex-none mr-(--logoloop-gap) text-(length:--logoloop-logoHeight) leading-none',
            scaleOnHover && 'overflow-visible group/item'
          ]"
          role="listitem"
        >
          <a
            v-if="item.href"
            :class="[
              'inline-flex items-center no-underline rounded',
              'transition-opacity duration-200 ease-linear',
              'hover:opacity-80',
              'focus-visible:outline focus-visible:outline-current focus-visible:outline-offset-2'
            ]"
            :href="item.href"
            :aria-label="item.ariaLabel"
            target="_blank"
            rel="noreferrer noopener"
          >
            <UiLogoContent :item="item" :scale-on-hover="scaleOnHover" />
          </a>
          <UiLogoContent v-else :item="item" :scale-on-hover="scaleOnHover" />
        </li>
      </ul>
    </motion.div>
  </div>
</template>

<script setup lang="ts">
import { motion } from 'motion-v'

export type LogoItem = {
  icon: string
  class?: string
  href?: string
  title?: string
  ariaLabel?: string
}

export interface LogoLoopProps {
  logos: LogoItem[]
  speed?: number
  direction?: 'left' | 'right'
  width?: number | string
  logoHeight?: number
  gap?: number
  pauseOnHover?: boolean
  fadeOut?: boolean
  fadeOutColor?: string
  scaleOnHover?: boolean
  ariaLabel?: string
  className?: string
  style?: string | Record<string, string | number>
}

const ANIMATION_CONFIG = {
  MIN_COPIES: 2,
  COPY_HEADROOM: 2
} as const

const props = withDefaults(defineProps<LogoLoopProps>(), {
  speed: 120,
  direction: 'left',
  width: '100%',
  logoHeight: 60,
  gap: 32,
  pauseOnHover: true,
  fadeOut: false,
  fadeOutColor: undefined,
  scaleOnHover: false,
  ariaLabel: 'Partner logos',
  className: undefined,
  style: undefined
})

const containerRef = useTemplateRef('containerRef')
const trackRef = useTemplateRef('trackRef')
const seqRef = ref<HTMLUListElement | null>(null)

const seqWidth = ref<number>(0)
const copyCount = ref<number>(ANIMATION_CONFIG.MIN_COPIES)
const currentX = ref<number>(0)
const animationKey = ref<number>(0)
const isHovered = ref<boolean>(false)
const isReady = ref<boolean>(false)

const animateX = computed(() => {
  if (!isReady.value || seqWidth.value === 0) return 0
  if (isHovered.value && props.pauseOnHover) return currentX.value
  return props.direction === 'left' ? -seqWidth.value : seqWidth.value
})

const animationDuration = computed(() => {
  if (!isReady.value || seqWidth.value === 0 || props.speed === 0) return 0
  if (isHovered.value && props.pauseOnHover) return 0
  return seqWidth.value / Math.abs(props.speed)
})

const transitionConfig = computed(() => {
  if (!isReady.value) {
    return {
      duration: 0
    }
  }

  if (isHovered.value && props.pauseOnHover) {
    return {
      duration: 0.4,
      ease: [0.4, 0, 0.2, 1] as const
    }
  }

  return {
    duration: animationDuration.value,
    ease: 'linear' as const,
    repeat: Infinity,
    repeatType: 'loop' as const
  }
})

const cssVariables = computed(() => ({
  '--logoloop-gap': `${props.gap}px`,
  '--logoloop-logoHeight': `${props.logoHeight}px`,
  ...(props.fadeOutColor && { '--logoloop-fadeColor': props.fadeOutColor })
}))

const rootClasses = computed(() => {
  const classes = [
    'relative overflow-x-hidden group',
    '[--logoloop-gap:32px]',
    '[--logoloop-logoHeight:28px]',
    '[--logoloop-fadeColorAuto:#ffffff]',
    'dark:[--logoloop-fadeColorAuto:#0b0b0b]'
  ]

  if (props.scaleOnHover) {
    classes.push('py-[calc(var(--logoloop-logoHeight)*0.1)]')
  }

  if (props.className) {
    classes.push(props.className)
  }

  return classes
})

const containerStyle = computed(() => ({
  width: typeof props.width === 'number' ? `${props.width}px` : props.width,
  ...cssVariables.value,
  ...(typeof props.style === 'object' && props.style !== null
    ? props.style
    : {})
}))

const handleMouseEnter = () => {
  if (props.pauseOnHover) {
    isHovered.value = true
  }
}

const handleMouseLeave = () => {
  if (props.pauseOnHover) {
    isHovered.value = false
    // Restart from current position
    animationKey.value++
  }
}

const handleAnimationUpdate = (latest: Record<string, unknown>) => {
  if (typeof latest.x === 'number') {
    currentX.value = latest.x
  }
}

const updateDimensions = async () => {
  await nextTick()
  const containerWidth = containerRef.value?.clientWidth ?? 0
  const sequenceWidth = seqRef.value?.getBoundingClientRect?.()?.width ?? 0

  if (sequenceWidth > 0) {
    seqWidth.value = Math.ceil(sequenceWidth)
    const copiesNeeded =
      Math.ceil(containerWidth / sequenceWidth) + ANIMATION_CONFIG.COPY_HEADROOM
    copyCount.value = Math.max(ANIMATION_CONFIG.MIN_COPIES, copiesNeeded)

    if (!isReady.value) {
      isReady.value = true
    }
    // Restart animation after measuring
    animationKey.value++
  }
}

let resizeObserver: ResizeObserver | null = null
const setupResizeObserver = () => {
  if (!window.ResizeObserver) {
    const handleResize = () => updateDimensions()
    window.addEventListener('resize', handleResize)
    updateDimensions()
    return () => window.removeEventListener('resize', handleResize)
  }

  resizeObserver = new ResizeObserver(updateDimensions)

  if (containerRef.value) {
    resizeObserver.observe(containerRef.value)
  }
  if (seqRef.value) {
    resizeObserver.observe(seqRef.value)
  }

  updateDimensions()

  return () => {
    resizeObserver?.disconnect()
    resizeObserver = null
  }
}

const setupImageLoader = () => {
  const images = seqRef.value?.querySelectorAll('img') ?? []

  if (images.length === 0) {
    updateDimensions()
    return
  }

  let remainingImages = images.length
  const handleImageLoad = () => {
    remainingImages -= 1
    if (remainingImages === 0) {
      updateDimensions()
    }
  }

  images.forEach((img) => {
    const htmlImg = img as HTMLImageElement
    if (htmlImg.complete) {
      handleImageLoad()
    } else {
      htmlImg.addEventListener('load', handleImageLoad, { once: true })
      htmlImg.addEventListener('error', handleImageLoad, { once: true })
    }
  })

  return () => {
    images.forEach((img) => {
      img.removeEventListener('load', handleImageLoad)
      img.removeEventListener('error', handleImageLoad)
    })
  }
}

let cleanupResize: (() => void) | undefined
let cleanupImages: (() => void) | undefined

const cleanup = () => {
  cleanupResize?.()
  cleanupImages?.()
}

onMounted(async () => {
  await nextTick()
  setTimeout(() => {
    cleanupResize = setupResizeObserver()
    cleanupImages = setupImageLoader()
  }, 10)
})

onUnmounted(() => {
  cleanup()
})

watch(
  [() => props.logos, () => props.gap, () => props.logoHeight],
  async () => {
    await nextTick()
    cleanupImages?.()
    cleanupImages = setupImageLoader()
    updateDimensions()
  },
  { deep: true }
)
</script>
